import { useCallback, useEffect, useMemo, useState } from "react"
import { DepositForAccount } from "@mezo/api"

import {
  initializeMigrationFlow,
  migrateDepositToTbtc,
  resetDepositActionState,
  selectDepositActionStatus,
  selectMigratableDeposits,
  setActionStatus,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"

import { useDepositCarousel } from "./deposit"

export const useDepositMigrationModal = (
  isOpen: boolean,
  onClose: () => void,
) => {
  const dispatch = useDappDispatch()
  const deposits = useDappSelector(selectMigratableDeposits)

  const depositsToShow = useMemo(
    () => deposits.filter((deposit) => !!deposit.depositId),
    [deposits],
  )

  const onDepositCarouselChangeCallback = useCallback(
    (_: number, nextIndex: number) => {
      const { id, depositId, token } = depositsToShow[nextIndex]

      dispatch(
        initializeMigrationFlow({ id, depositId, token, actionStatus: "idle" }),
      )
    },
    [depositsToShow, dispatch],
  )

  const {
    currentlySelectedDeposit,
    setCurrentlySelectedDeposit,
    onDepositCarouselChange,
  } = useDepositCarousel(depositsToShow, onDepositCarouselChangeCallback)

  const [displaySuccessModal, setDisplaySuccessModal] = useState(false)

  const onSuccessfulMigration = () => {
    setDisplaySuccessModal(true)
  }

  useEffect(() => {
    if (isOpen) {
      dispatch(resetDepositActionState())
    }
  }, [isOpen, dispatch])

  const closeModalHandler = useCallback(() => {
    if (setCurrentlySelectedDeposit && deposits && deposits?.length > 0) {
      setCurrentlySelectedDeposit(deposits[0])
    }
    setDisplaySuccessModal(false)
    dispatch(resetDepositActionState())
    onClose()
  }, [setCurrentlySelectedDeposit, deposits, dispatch, onClose])

  return {
    deposits: depositsToShow,
    currentlySelectedDeposit,
    onDepositCarouselChange,
    displaySuccessModal,
    onSuccessfulMigration,
    closeModal: closeModalHandler,
  }
}

export const useDepositMigrationModalForm = (
  deposit: DepositForAccount | undefined,
) => {
  const dispatch = useDappDispatch()
  const depositActionStatus = useDappSelector(selectDepositActionStatus)

  const migrateDepositHandler = useCallback(
    async (onSuccess?: () => void) => {
      if (!deposit) return

      dispatch(setActionStatus("in-progress"))

      const { payload: success } = await dispatch(
        migrateDepositToTbtc({
          depositUuid: deposit.id,
          depositId: deposit.depositId,
          token: deposit.token,
        }),
      )

      if (success) {
        dispatch(setActionStatus("completed"))
        if (onSuccess) onSuccess()
      } else {
        dispatch(setActionStatus("error"))
      }
    },
    [dispatch, deposit],
  )

  return {
    migrationStatus: depositActionStatus,
    migrateDeposit: migrateDepositHandler,
  }
}
